import type { MouseEventHandler } from 'react';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { intersectionBy } from 'lodash-es';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import Pagination from 'components/stateless/Pagination/Pagination';
import SearchBorder from 'components/stateless/SearchForm/SearchBorder';
import SearchEmpty from 'components/stateless/SearchForm/SearchEmpty';
import SearchInput from 'components/stateless/SearchForm/SearchInput';
import SearchLabel from 'components/stateless/SearchForm/SearchLabel';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import type { HeaderType } from 'components/stateless/Table/TableHeader';
import TableHeader from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import TdLink from 'components/stateless/Table/TdLink';
import Tr from 'components/stateless/Table/Tr';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { ROUTES_FI } from 'constants/routes/financier';
import { REQUEST_RESULT } from 'enums';
import type { PageableType } from 'hooks/usePageable';
import type Pageable from 'models/Pageable';
import type { AnchorDealerVOModel } from 'models/vo/AnchorDealerVO';
import type { BranchVOModel } from 'models/vo/BranchVO';
import type { FinancierAnchorDealerListRequest } from 'utils/http/api/financier/anchor-dealers/request';
import { ModalSize, ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';
import { tableValueManage } from 'utils/valueManager/ValueManager';

import AssignBranchModal from '../AssignBranchModal';

interface FinancierAnchorDealerAssignmentListAdminWaitingTabProps {
  waitingAnchorDealerList: Pageable<AnchorDealerVOModel[]> | undefined;
  pageable: PageableType;
  paginate(page: number, sizePerPage: number): void;
  handleClickSearch: MouseEventHandler<HTMLButtonElement>;
  assignRequest(branchId: number, anchorDealerIds: number[]): Promise<REQUEST_RESULT>;
}

function FinancierAnchorDealerAssignmentListAdminWaitingTab({
  waitingAnchorDealerList,
  pageable,
  paginate,
  handleClickSearch,
  assignRequest,
}: FinancierAnchorDealerAssignmentListAdminWaitingTabProps) {
  const { t } = useTranslation();
  const modal = useModal();
  const { register, reset } = useFormContext<FinancierAnchorDealerListRequest>();
  const [checkedAnchorDealerData, setCheckedAnchorDealerData] = useState<AnchorDealerVOModel[]>([]);

  useEffect(() => {
    setCheckedAnchorDealerData([]);
  }, [pageable.currentPage]);

  useEffect(() => {
    const newCheckedWaitingAnchorDealer = intersectionBy(
      waitingAnchorDealerList?.content,
      checkedAnchorDealerData,
      'id',
    );
    setCheckedAnchorDealerData(newCheckedWaitingAnchorDealer);
  }, [pageable.sizePerPage]);

  const handleClickAllCheckBox = (e: any) => {
    if (waitingAnchorDealerList?.content === undefined) return;

    if (e.target.checked) {
      const checkedCheckboxes = [...checkedAnchorDealerData];

      waitingAnchorDealerList?.content.forEach(async item => {
        if (checkedAnchorDealerData.map(data => data.id).indexOf(item.id) < 0) {
          checkedCheckboxes.push(item);
        }
      });

      setCheckedAnchorDealerData(checkedCheckboxes);
    } else {
      setCheckedAnchorDealerData([]);
    }
  };

  const WAITING_FOR_CONFIRMATION_TABLE_HEADERS: HeaderType[] = [
    {
      headerText: '',
      className: 'text-center',
      colWidths: 50,
      hasCheckBox: true,
      handleClickAllCheckBox: handleClickAllCheckBox,
      checkBoxId: String(pageable.currentPage),
      isAllChecked: checkedAnchorDealerData.length === waitingAnchorDealerList?.content.length,
    },
    {
      headerText: t('text:Uploaded_Dealer_Name'),
      colWidths: 100,
    },
    {
      headerText: t('text:Uploaded_Dealer_Tax_Code'),
      colWidths: 120,
    },
    {
      headerText: t('text:Associated_Anchor_Name'),
      colWidths: 100,
    },
    {
      headerText: t('text:Legal_Representative_Name'),
      colWidths: 120,
    },
    {
      headerText: t('text:Legal_Representative_Email'),
      colWidths: 100,
    },
    {
      headerText: t('text:Telephone'),
      colWidths: 100,
    },
    {
      headerText: t('text:Registered_Office_Address'),
      colWidths: 100,
    },
    {
      headerText: '',
      colWidths: 50,
    },
  ];

  const onChangeCheckbox = (e: any, item: AnchorDealerVOModel): void => {
    if (e.target.checked) {
      setCheckedAnchorDealerData(prev => {
        const newArr = [...prev];

        newArr.push(item);

        return newArr;
      });
    } else {
      setCheckedAnchorDealerData(prev => {
        const newArr = [...prev];

        newArr.splice(prev.map(data => data.id).indexOf(item.id), 1);

        return newArr;
      });
    }
  };

  const handleClickAssignBranchButton = () => {
    const resultModal = (branchName: string) => {
      modal.show(
        <h6>
          [{checkedAnchorDealerData.length}]{' '}
          {t('text:Dealer(s)_is(are)_assigned_to_the_branchName_Branch', {
            branchName: branchName,
          })}
        </h6>,
        {
          modalType: ModalType.ALERT,
          closeBtnText: t('text:OK'),
          closeBtnCb: () => {
            setCheckedAnchorDealerData([]);
          },
        },
      );
    };
    const showCheckModal = (branchInfo: BranchVOModel) => {
      modal.show(
        <h6>
          {t('text:Would_you_like_to_assign_the_dealer(s)_to_the_following_branch?')}

          <div className="mt-3">
            <div>
              {t('text:Branch_Name')}: {tableValueManage(branchInfo.branchName)}
            </div>
            <div>
              {t('text:Branch_Legal_Representative_Name')}: {tableValueManage(branchInfo.managerName)}
            </div>
            <div>
              {t('text:Branch_Registered_Office')}: {tableValueManage(branchInfo.address)}
            </div>
          </div>
        </h6>,
        {
          title: t('text:Select_Branch'),
          modalType: ModalType.CONFIRM,
          closeBtnText: t(`text:Cancel`),
          confirmBtnCb: async () => {
            const isSuccess = await assignRequest(
              branchInfo.branchId,
              checkedAnchorDealerData.map(data => data.id),
            );
            if (isSuccess === REQUEST_RESULT.SUCCESS) {
              resultModal(branchInfo.branchName);
              setCheckedAnchorDealerData([]);
            }
          },
        },
      );
    };
    modal.show(
      <AssignBranchModal selectButtonCb={branchInfo => showCheckModal(branchInfo)} currentModalId={modal.id} />,
      {
        modalSize: ModalSize.XL,
        title: t('text:Branch_Assignment'),
        closeBtnText: t('text:Close'),
      },
    );
  };

  return (
    <>
      <div className="content-area">
        <form>
          <SectionTitle title={t('text:Search')}>
            <Button
              variant={ButtonVariantEnum.OUTLINED}
              color={ButtonColorEnum.SECONDARY}
              onClick={e => {
                e.preventDefault();
                reset({});
              }}
            >
              {t('text:Remove_Filter')}
            </Button>
          </SectionTitle>
          <SearchBorder>
            <div className="row">
              <SearchLabel label={t('text:Uploaded_Dealer_Name')} />
              <SearchInput name="name" ref={register} />
              <SearchLabel label={t('text:Uploaded_Dealer_Tax_Code')} />
              <SearchInput name="taxCode" ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Associated_Anchor_Name')} />
              <SearchInput name="anchorClientName" ref={register} />
              <SearchLabel label={t('text:Legal_Representative_Name')} />
              <SearchInput name="representativeName" ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Legal_Representative_Email')} />
              <SearchInput name="representativeEmail" ref={register} />
              <SearchLabel label={t('text:Telephone')} />
              <SearchInput name="telephone" ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Registered_Office_Address')} />
              <SearchInput name="address" ref={register} />
              <SearchEmpty />
            </div>
          </SearchBorder>
          <div className="flex-center mt-4">
            <Button size={ButtonSizeEnum.LG} onClick={handleClickSearch}>
              {t('text:Search')}
            </Button>
          </div>
        </form>
      </div>
      <div className="division-border" />
      <div className="content-area">
        <SectionTitle title={t('text:Result')} />
        <p className="total-data">
          {t('text:Total')} {waitingAnchorDealerList?.totalElements ?? 0}
        </p>
        <TableBorder>
          <TableHeader header={WAITING_FOR_CONFIRMATION_TABLE_HEADERS} />
          <TableBody numOfCol={WAITING_FOR_CONFIRMATION_TABLE_HEADERS?.length}>
            {waitingAnchorDealerList?.content.map((item, index) => (
              <Tr key={index}>
                <Td>
                  <div className="text-center">
                    <input
                      className="form-check-input m-0"
                      type="checkbox"
                      onChange={e => onChangeCheckbox(e, item)}
                      checked={checkedAnchorDealerData.map(item => item.id).includes(item.id)}
                    />
                  </div>
                </Td>
                <Td data={item.name} />
                <Td data={item.taxCode} />
                <Td data={item.anchorName} />
                <Td data={item.representativeName} />
                <Td data={item.representativeEmail} />
                <Td data={item.telephone} />
                <Td data={item.address} />
                <TdLink path={ROUTES_FI.MANAGE_DEALER.ASSIGNMENT_DETAIL_BUILD_PATH(item.id)} />
              </Tr>
            ))}
          </TableBody>
        </TableBorder>
        <Pagination pageable={pageable} paginate={paginate} />
      </div>
      <div className="content-area flex-end">
        <Button
          size={ButtonSizeEnum.LG}
          onClick={handleClickAssignBranchButton}
          disabled={checkedAnchorDealerData.length === 0}
        >
          {t('text:Assign_Branch')}
        </Button>
      </div>
    </>
  );
}

export default FinancierAnchorDealerAssignmentListAdminWaitingTab;
